import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatasetConfirmationView } from './DatasetConfirmationView';
import { CSVConfirmationView } from './CSVConfirmationView';
import { useToast } from '../../hooks/useToast';
import { apiClient } from '../../lib/api';
import { parseCSVForPreview } from '../../utils/fileUtils';
import { ToastContainer } from '../ui/ToastContainer';
import { useAppData } from '../../contexts/AppDataContext';
import { useAuth } from '../../contexts/AuthContext';
import { getSearchTemplates, SearchTemplate } from '../../utils/icpTemplates';
import { SeasonalBackground } from '../ui/SeasonalBackground';
import { PLAN_NAME_FREE, FREE_TIER_LEAD_LIMIT, MAX_LEADS_PER_REQUEST } from '../../constants/subscription';
import { parseError } from '../../utils/errorUtils';
import OutOfCreditsModal from '../settings/credits/OutOfCreditsModal';
import {
  HeroSection,
  SearchInputCard,
  WorkflowCarousel,
  WorkflowExample,
  RecentDatasetsSection,
  MonitorsSection,
  CampaignsSection,
  HomeFooter,
} from './home';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { useSuggestionWizard } from '../../contexts/SuggestionWizardContext';
import { Monitor } from '../../types/monitoring';
import { generateWorkflowExamples } from '../../constants/workflowExamples';
import { MonitorModal } from '../layout/MonitorModal';
import { CampaignModal } from '../layout/CampaignModal';
import { useTourMockData } from '../../hooks/useTourMockData';

const PLACEHOLDER_CYCLE_MS = 4000;

interface ChatMessage {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  isLoading?: boolean;
  timestamp?: Date;
  criteria?: string[]; // For displaying criteria pills in confirmation messages
}

interface DefaultScreenProps {
  onCreateDataset: (
    searchQuery: string | null,
    criteria: string[] | null,
    tableType: 'companies' | 'people' | 'custom',
    customType: string,
    rowCount: number,
    uploadId: string | null,
    title: string | null
  ) => void;
  leadGenerationError?: string | null;
  isLoading?: boolean;
  onDatasetCreated?: (tableId: string) => void;
  initialSearchQuery?: string;
  initialTableType?: 'companies' | 'people' | 'custom';
  initialPreviewData?: any;
  onSearchQueryConsumed?: () => void;
}

const ROW_COUNT_OPTIONS = [10, 25, 50, 100];

export const DefaultScreen: React.FC<DefaultScreenProps> = ({
  onCreateDataset,
  isLoading = false,
  leadGenerationError = null,
  onDatasetCreated,
  initialSearchQuery,
  initialTableType,
  initialPreviewData,
  onSearchQueryConsumed,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscriptionData, datasets, refreshDatasets } = useAppData();
  const { toasts, showToast, removeToast } = useToast();
  const { openSettings } = useSettingsModal();
  const { openWizard } = useSuggestionWizard();
  const { isTourMode, mockDataset } = useTourMockData();

  // Show only mock dataset when tour is active
  const displayDatasets = isTourMode && mockDataset ? [mockDataset] : datasets;

  const [searchQuery, setSearchQuery] = useState('');
  const [tableType, setTableType] = useState<'companies' | 'people' | 'custom'>('companies');
  const [customType, setCustomType] = useState('');
  const [rowCount, setRowCount] = useState(10);
  const [customRowCount, setCustomRowCount] = useState('');
  const [showInputWarning, setShowInputWarning] = useState(false);
  const [searchTemplates, setSearchTemplates] = useState<SearchTemplate[]>(() => getSearchTemplates(null));

  // CSV file attachment state
  const [showCSVConfirmation, setShowCSVConfirmation] = useState(false);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [attachedFileInfo, setAttachedFileInfo] = useState<{ rowCount: number; columnCount: number } | null>(null);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isPlaceholderVisible, setIsPlaceholderVisible] = useState(true);
  const [workflowExamples, setWorkflowExamples] = useState<WorkflowExample[]>(() =>
    generateWorkflowExamples(null)
  );
  const [animationsEnabled, setAnimationsEnabled] = useState(() => {
    const saved = localStorage.getItem('signaliq_animations_enabled');
    return saved !== null ? saved === 'true' : true;
  });
  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [campaigns, setCampaigns] = useState<Array<{
    id: string;
    name: string;
    subject: string;
    status: 'active' | 'paused' | 'draft' | 'completed';
    stats: {
      sent: number;
      delivered?: number;
      bounced?: number;
    };
  }>>([]);
  const [showMonitorModal, setShowMonitorModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);

  // Confirmation view state
  const [showConfirmationView, setShowConfirmationView] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isCreatingDataset, setIsCreatingDataset] = useState(false);
  const [showOutOfCreditsModal, setShowOutOfCreditsModal] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [datasetGenerationError, setDatasetGenerationError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const focusSearchInput = useRef<(() => void) | null>(null);

  const isFreeTier = subscriptionData?.plan_name === PLAN_NAME_FREE;

  // Handle initial search query from signal generator wizard
  useEffect(() => {
    if (initialSearchQuery) {
      setSearchQuery(initialSearchQuery);
      if (initialTableType) {
        setTableType(initialTableType);
      }

      if (initialPreviewData) {
        // Auto-preview: jump straight to confirmation view
        setPreviewData(initialPreviewData);
        setShowConfirmationView(true);
      } else {
        // Focus the search input after setting the query
        setTimeout(() => focusSearchInput.current?.(), 100);
      }

      // Notify parent that we've consumed the initial query
      onSearchQueryConsumed?.();
    }
  }, [initialSearchQuery, initialTableType, initialPreviewData, onSearchQueryConsumed]);

  // Listen for tour event to exit confirmation view
  useEffect(() => {
    const handleExitConfirmation = () => {
      setShowConfirmationView(false);
      setPreviewData(null);
      setChatMessages([]);
    };

    window.addEventListener('tour-exit-confirmation-view', handleExitConfirmation);
    return () => window.removeEventListener('tour-exit-confirmation-view', handleExitConfirmation);
  }, []);

  useEffect(() => {
    const fetchICPProfiles = async () => {
      try {
        const profiles = await apiClient.getICPProfiles();
        setSearchTemplates(getSearchTemplates(profiles));
        if (profiles && profiles.length > 0) {
          setWorkflowExamples(generateWorkflowExamples(profiles[0]));
        }
      } catch (error) {
        console.error('Failed to fetch ICP profiles:', error);
      }
    };
    fetchICPProfiles();
  }, []);

  useEffect(() => {
    if (searchQuery) return;
    const cycleInterval = setInterval(() => {
      setIsPlaceholderVisible(false);
      setTimeout(() => {
        setPlaceholderIndex((prev) => (prev + 1) % searchTemplates.length);
        setIsPlaceholderVisible(true);
      }, 300);
    }, PLACEHOLDER_CYCLE_MS);
    return () => clearInterval(cycleInterval);
  }, [searchQuery, searchTemplates.length]);

  useEffect(() => {
    const fetchMonitors = async () => {
      try {
        const response = await apiClient.listMonitors();
        setMonitors(response.monitors || []);
      } catch (error) {
        console.error('Failed to fetch monitors:', error);
      }
    };
    fetchMonitors();
  }, []);

  // Fetch campaigns from the API
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await apiClient.listCampaigns({ limit: 3 });
        // Transform Campaign[] to expected format for CampaignsSection
        // Note: Campaign type doesn't have full stats, only recipients_count
        const transformed = (response.campaigns || []).map((campaign: any) => ({
          id: campaign.id,
          name: campaign.name,
          subject: campaign.email_subject || campaign.name,
          status: campaign.status as 'active' | 'paused' | 'draft' | 'completed',
          stats: {
            sent: campaign.recipients_count || 0,
            delivered: 0, // Not available from API - placeholder
            bounced: 0,   // Not available from API - placeholder
          },
        }));
        setCampaigns(transformed);
      } catch (error) {
        console.error('Failed to fetch campaigns:', error);
        setCampaigns([]);
      }
    };
    fetchCampaigns();
  }, []);

  const toggleAnimations = () => {
    const newValue = !animationsEnabled;
    setAnimationsEnabled(newValue);
    localStorage.setItem('signaliq_animations_enabled', String(newValue));
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    const name = user?.firstName ? `, ${user.firstName}` : '';
    if (hour < 12) return `Good morning${name}`;
    if (hour < 18) return `Good afternoon${name}`;
    return `Good evening${name}`;
  };

  // Handle CSV file attachment
  const handleFileAttach = (file: File) => {
    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      showToast('Invalid file type. Please upload a CSV file.', 'error');
      return;
    }

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      showToast('File size exceeds the limit of 50MB.', 'error');
      return;
    }

    // Parse to get row/column count
    parseCSVForPreview(file, (parsedRowCount, columns) => {
      setAttachedFile(file);
      setAttachedFileInfo({ rowCount: parsedRowCount, columnCount: columns.length });
      // Auto-set the search query to import message
      if (!searchQuery.trim()) {
        setSearchQuery(`Import ${file.name.replace('.csv', '')} as a table`);
      }
    }, (error) => {
      console.error('Error parsing CSV:', error);
      showToast('Error reading file. Please try again.', 'error');
    });
  };

  // Remove attached file
  const handleFileRemove = () => {
    setAttachedFile(null);
    setAttachedFileInfo(null);
    setSearchQuery('');
  };

  const handleSubmit = async () => {
    if (isLoading || isLoadingPreview) return;

    // Handle CSV file attachment - show chat-based confirmation view
    if (attachedFile) {
      setShowCSVConfirmation(true);
      return;
    }

    // Normal search flow
    if (!searchQuery.trim()) return;

    setIsLoadingPreview(true);
    try {
      const preview = await apiClient.previewDataset(searchQuery.trim(), tableType, customType);
      setPreviewData(preview);
      // Use inline confirmation view instead of modal
      setShowConfirmationView(true);
    } catch (error: any) {
      showToast(error?.response?.data?.detail || 'Failed to preview dataset', 'error', 5000);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleConfirmGeneration = async (
    sessionId: string | null = null,
    skipSearch = false,
    uploadId: string | null = null,
    fileName: string | null = null
  ) => {
    if (!skipSearch && !searchQuery.trim()) {
      setShowConfirmationView(false);
      return;
    }
    const finalRowCount = customRowCount ? parseInt(customRowCount) : rowCount;

    // For CSV uploads, use the old flow (navigate to dataset)
    if (skipSearch && uploadId && fileName) {
      setShowConfirmationView(false);
      onCreateDataset(null, null, tableType, customType, finalRowCount, uploadId, fileName);
      return;
    }

    // For search-based datasets, create and show ResearchView
    setIsCreatingDataset(true);
    setDatasetGenerationError(null); // Clear any previous error

    try {
      const criteria = previewData?.search?.criteria?.map((c: { description: string }) => c.description) || [];
      const response = await apiClient.generateEnhancedLeadTable(
        searchQuery.trim(),
        criteria,
        tableType,
        finalRowCount,
        customType,
        null,
        null
      );

      if (response.table_id) {
        // Link chat session to dataset if sessionId is available
        if (sessionId) {
          try {
            await apiClient.linkChatToDataset(response.table_id, sessionId);
          } catch (linkError) {
            console.error('Error linking chat session to dataset:', linkError);
          }
        }

        // Refresh datasets cache
        await refreshDatasets();

        // Navigate directly to the dataset page with initial chat messages
        // The dataset page has all the proper UI: progress bar, chat panel, real-time updates
        setShowConfirmationView(false);
        navigate(`/datasets/${response.table_id}`, {
          state: { initialChatMessages: chatMessages }
        });

        // Notify parent if callback provided
        onDatasetCreated?.(response.table_id);
      } else {
        showToast('Failed to create dataset. Please try again.', 'error', 5000);
      }
    } catch (error: unknown) {
      console.error('Error creating dataset:', error);
      const parsedError = parseError(error);
      const errorMessage = parsedError.details
        ? `${parsedError.message} ${parsedError.details}`
        : parsedError.message;
      setDatasetGenerationError(errorMessage);
      if (parsedError.shouldReturnToIntro) {
        showToast(errorMessage, {
          type: 'error',
          duration: 6000,
          action: {
            label: 'Upgrade',
            onClick: () => openSettings('billing'),
          },
        });
      } else if (parsedError.requiresUpgrade) {
        showToast(errorMessage, 'error', 5000);
        setShowOutOfCreditsModal(true);
      } else {
        showToast(errorMessage, 'error', 5000);
      }
    } finally {
      setIsCreatingDataset(false);
    }
  };

  // Handler for going back from confirmation view
  const handleBackFromConfirmation = () => {
    setShowConfirmationView(false);
    setDatasetGenerationError(null); // Clear error when going back
  };

  // Handler for query changes in confirmation view
  const handleConfirmationQueryChange = async (newQuery: string) => {
    setSearchQuery(newQuery);
    if (newQuery.trim()) {
      setIsLoadingPreview(true);
      try {
        const preview = await apiClient.previewDataset(newQuery.trim(), tableType, customType);
        setPreviewData(preview);
      } catch (error) {
        console.error('Error updating preview:', error);
      } finally {
        setIsLoadingPreview(false);
      }
    }
  };

  const handleCustomRowCountChange = (value: string) => {
    const digits = value.replace(/\D/g, '');
    setCustomRowCount(digits);
    const max = isFreeTier ? FREE_TIER_LEAD_LIMIT : MAX_LEADS_PER_REQUEST;
    setShowInputWarning(digits ? parseInt(digits) > max : false);
  };

  const handleWorkflowClick = (example: WorkflowExample) => {
    setSearchQuery(example.query);
    setTableType(example.type);
    focusSearchInput.current?.();
  };

  const handleSearchInputReady = useCallback((focus: () => void) => {
    focusSearchInput.current = focus;
  }, []);

  const effectiveRowCount = customRowCount ? parseInt(customRowCount) : rowCount;

  // Show CSV confirmation view if user attached a CSV and clicked Run
  if (showCSVConfirmation && attachedFile) {
    return (
      <>
        <CSVConfirmationView
          file={attachedFile}
          tableType={tableType}
          customType={customType}
          recordCount={effectiveRowCount}
          onConfirm={(uploadId, fileName) => {
            setShowCSVConfirmation(false);
            setAttachedFile(null);
            setAttachedFileInfo(null);
            setSearchQuery('');
            handleConfirmGeneration(null, true, uploadId, fileName);
          }}
          onBack={() => {
            setShowCSVConfirmation(false);
          }}
        />
        <ToastContainer toasts={toasts} onRemove={removeToast} />
      </>
    );
  }

  // Show confirmation view if we have preview data and user clicked Run
  // Also show it while creating dataset to prevent blank screen
  if ((showConfirmationView || isCreatingDataset) && previewData) {
    return (
      <>
        <DatasetConfirmationView
          previewData={previewData}
          searchQuery={searchQuery}
          rowCount={effectiveRowCount}
          tableType={tableType}
          customType={customType}
          isLoadingPreview={isLoadingPreview}
          onConfirm={(sessionId) => handleConfirmGeneration(sessionId)}
          onBack={handleBackFromConfirmation}
          onSearchQueryChange={handleConfirmationQueryChange}
          onTableTypeChange={setTableType}
          onCustomTypeChange={setCustomType}
          onRowCountChange={(count) => {
            setRowCount(count);
            setCustomRowCount('');
          }}
          chatMessages={chatMessages}
          onChatMessagesChange={setChatMessages}
          generationError={datasetGenerationError}
        />
        <ToastContainer toasts={toasts} onRemove={removeToast} />
        <OutOfCreditsModal isOpen={showOutOfCreditsModal} onClose={() => setShowOutOfCreditsModal(false)} />
      </>
    );
  }

  // Default: show home screen
  return (
    <div ref={scrollRef} className="flex-1 overflow-auto bg-neutral-50 relative">
      <SeasonalBackground enabled={animationsEnabled} />

      <div className="max-w-[1120px] mx-auto px-4 sm:px-10 md:px-16 lg:px-20 xl:px-24 pt-4 sm:pt-16 pb-6 relative z-[1]">
        <section className="mb-6">
          <HeroSection
            greeting={getGreeting()}
            animationsEnabled={animationsEnabled}
            onToggleAnimations={toggleAnimations}
          />

          <SearchInputCard
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            tableType={tableType}
            onTableTypeChange={setTableType}
            customType={customType}
            onCustomTypeChange={setCustomType}
            rowCount={rowCount}
            onRowCountChange={setRowCount}
            customRowCount={customRowCount}
            onCustomRowCountChange={handleCustomRowCountChange}
            showInputWarning={showInputWarning}
            isLoading={isLoading}
            isLoadingPreview={isLoadingPreview}
            onSubmit={handleSubmit}
            onFileAttach={handleFileAttach}
            onFileRemove={handleFileRemove}
            attachedFile={attachedFile}
            attachedFileInfo={attachedFileInfo}
            searchTemplates={searchTemplates}
            placeholderIndex={placeholderIndex}
            isPlaceholderVisible={isPlaceholderVisible}
            rowCountOptions={ROW_COUNT_OPTIONS}
            onReady={handleSearchInputReady}
            onOpenWizard={openWizard}
          />

          {leadGenerationError && (
            <p className="mt-3 text-[13px] text-red-600 bg-red-50 px-3 py-2 rounded-md">
              {leadGenerationError}
            </p>
          )}

          <WorkflowCarousel examples={workflowExamples} onExampleClick={handleWorkflowClick} pauseAutoRotate={isTourMode} />
        </section>

        <RecentDatasetsSection
          datasets={displayDatasets || []}
          onNewDataset={() => {
            scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
            setTimeout(() => focusSearchInput.current?.(), 300);
          }}
        />

        <MonitorsSection
          monitors={monitors}
          onOpenSettings={openSettings}
          onNewMonitor={() => setShowMonitorModal(true)}
        />

        <CampaignsSection
          campaigns={campaigns}
          onNewCampaign={() => setShowCampaignModal(true)}
        />

        <HomeFooter />
      </div>

      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <MonitorModal
        isOpen={showMonitorModal}
        onClose={() => setShowMonitorModal(false)}
        datasets={datasets || []}
        onNewDataset={() => {
          setShowMonitorModal(false);
          scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
          setTimeout(() => focusSearchInput.current?.(), 300);
        }}
      />

      <CampaignModal
        isOpen={showCampaignModal}
        onClose={() => setShowCampaignModal(false)}
        datasets={datasets || []}
        onNewCampaign={() => {
          setShowCampaignModal(false);
          scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
          setTimeout(() => focusSearchInput.current?.(), 300);
        }}
      />

      <OutOfCreditsModal isOpen={showOutOfCreditsModal} onClose={() => setShowOutOfCreditsModal(false)} />
    </div>
  );
};
